// #ux-redesign-start: Modal primitive (Paso 2)
// Wrapper sobre Reactstrap Modal con tokens del DS aplicados.
// En mobile (<md) se convierte en bottom-sheet automáticamente.
//
// PORTABILIDAD: trasplantable a Web-Patients.
//
// USO:
//   <Modal isOpen={open} onClose={() => setOpen(false)} title="Studie zuweisen" size="md">
//     {body}
//     <Modal.Footer>
//       <Button variant="secondary" onClick={...}>Abbrechen</Button>
//       <Button variant="primary" onClick={...}>Zuweisen</Button>
//     </Modal.Footer>
//   </Modal>
// ─────────────────────────────────────────────────────────────────────────────

import React from 'react';
import PropTypes from 'prop-types';
import { Modal as RsModal, ModalBody, ModalHeader, ModalFooter as RsModalFooter } from 'reactstrap';
import { useTranslate } from 'react-translate';
import useResponsive from 'hooks/useResponsive';

const Modal = ({
  isOpen,
  onClose,
  title,
  size = 'md',
  closeOnBackdrop = true,
  closeOnEsc = true,
  bottomSheetOnMobile = true,
  className = '',
  children,
  ...rest
}) => {
  const ta = useTranslate('a11y');
  const { isMobile } = useResponsive();
  const isSheet = bottomSheetOnMobile && isMobile;

  const classes = [
    'ds-modal',
    `ds-modal--size-${size}`,
    isSheet ? 'ds-modal--sheet' : '',
    className,
  ].filter(Boolean).join(' ');

  return (
    <RsModal
      isOpen={isOpen}
      toggle={closeOnBackdrop ? onClose : undefined}
      keyboard={closeOnEsc}
      size={size === 'sm' ? 'sm' : size === 'lg' ? 'lg' : undefined}
      className={classes}
      backdropClassName="ds-modal__backdrop"
      contentClassName="ds-modal__content"
      {...rest}
    >
      {title && (
        <ModalHeader
          className="ds-modal__header"
          /* #ux-redesign: close button propio del DS. Evita la X roja heredada
             del template legacy (`.modal .modal-header .close` → #ff3636) y la
             posición absoluta que se montaba sobre el título. */
          close={
            <button
              type="button"
              className="ds-modal__close"
              aria-label={ta('close')}
              onClick={onClose}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          }
        >
          <span className="ds-modal__title">{title}</span>
        </ModalHeader>
      )}
      <ModalBody className="ds-modal__body">
        {children}
      </ModalBody>
    </RsModal>
  );
};

const Footer = ({ children, className = '', ...rest }) => (
  <RsModalFooter className={`ds-modal__footer ${className}`} {...rest}>
    {children}
  </RsModalFooter>
);

Modal.Footer = Footer;

Modal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.node,
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  closeOnBackdrop: PropTypes.bool,
  closeOnEsc: PropTypes.bool,
  bottomSheetOnMobile: PropTypes.bool,
  className: PropTypes.string,
  children: PropTypes.node,
};

export default Modal;
// #ux-redesign-end
